import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SigninBoxComponent } from '../shared/components/signinbox/signinbox.component';
import { SettingsBoxComponent } from '../shared/components/settingsbox/settingsbox.component';
import { LanguageSwitcherComponent } from '../shared/components/language-switcher/language-switcher.component';
import { I18nService } from '../shared/i18n/i18n.service';
import { UserTree } from '../shared/models/user.model';
import { AuthService } from '../shared/services/auth.service';
import { UserService } from '../shared/services/user.service';

/**
 * Nagłówek stron poza drzewem (strona główna, regulamin, logowanie).
 *
 * Nazwa serwisu jest linkiem. Zwykle prowadzi na stronę główną, ale gdy ktoś
 * zalogowany stoi właśnie na niej, kliknięcie w nazwę nie miałoby sensu -
 * wtedy zabiera go do jego drzewa (domyślnego, a gdy nie ma domyślnego,
 * pierwszego z listy).
 */
@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],
    standalone: true,
    imports: [RouterModule, MatTooltipModule, SigninBoxComponent, SettingsBoxComponent,
        LanguageSwitcherComponent]
})
export class HeaderComponent implements OnInit, OnDestroy {

  /** Drzewo, do którego wraca zalogowany użytkownik ze strony głównej. */
  private myTree: UserTree | undefined;
  private authSubscription: Subscription | undefined;
  private routerSubscription: Subscription | undefined;

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private i18n: I18nService,
    private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.loadTrees();
    this.authSubscription = this.authService.authStateChanged.subscribe(() => this.loadTrees());
    // adres decyduje, dokąd prowadzi nazwa serwisu
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.cdr.detectChanges());
  }

  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  /** Czy jesteśmy na stronie głównej - tam link na stronę główną nic nie da. */
  private get onLandingPage() {
    const url = this.router.url.split('?')[0].split('#')[0];
    return url === '/' || url === '';
  }

  get titleLink(): unknown[] {
    return (this.onLandingPage && this.myTree?.url) ? ['/tree', this.myTree.url] : ['/'];
  }

  get titleTooltip() {
    return (this.onLandingPage && this.myTree?.url)
      ? this.i18n.t('header.goToTree', { tree: this.myTree.title ?? '' })
      : this.i18n.t('common.homePage');
  }

  private loadTrees() {
    if (!this.authService.isLoggedIn()) {
      this.myTree = undefined;
      this.cdr.detectChanges();
      return;
    }

    this.userService.getUserTrees().subscribe({
      next: (trees) => {
        const list = (trees ?? []).filter((tree) => !!tree.url);
        this.myTree = list.find((tree) => tree.def === true) ?? list[0];
        this.cdr.detectChanges();
      },
      error: () => {
        this.myTree = undefined;
        this.cdr.detectChanges();
      }
    });
  }
}
